import styled, { useTheme } from "styled-components";
import type { DefaultTheme } from "styled-components";
import type { AuditEntry } from "../types";
import { formatAikaleima } from "../utils/format";
import { useTranslation } from "react-i18next";
import { normalizeTimestamp } from "../utils/dateUtils";

interface TyyppiMeta {
  teksti: string;
  bg: (theme: DefaultTheme) => string;
  fg: (theme: DefaultTheme) => string;
}

const tyyppiMeta: Record<string, TyyppiMeta> = {
  lukitus_vapautettu: {
    teksti: "ID-proofing-lukitus vapautettu",
    bg: (t) => t.colors.successSecondary,
    fg: (t) => t.colors.successDark1,
  },
  kayttajatunnus_lukittu: {
    teksti: "Käyttäjätunnus lukittu",
    bg: (t) => t.colors.alertLight1,
    fg: (t) => t.colors.alertBase,
  },
  kayttajatunnus_vapautettu: {
    teksti: "Käyttäjätunnus vapautettu",
    bg: (t) => t.colors.successSecondary,
    fg: (t) => t.colors.successDark1,
  },
  henkilollisyys_todennettu: {
    teksti: "Henkilöllisyys todennettu",
    bg: (t) => t.colors.successSecondary,
    fg: (t) => t.colors.successDark1,
  },
  kayttaja_poistettu: {
    teksti: "Käyttäjä poistettu",
    bg: (t) => t.colors.alertLight1,
    fg: (t) => t.colors.alertBase,
  },
  tiedot_muokattu: {
    teksti: "Tietoja muokattu",
    bg: (t) => t.colors.highlightLight2,
    fg: (t) => t.colors.highlightDark1,
  },
  tili_yhdistetty: {
    teksti: "Tili yhdistetty",
    bg: (t) => t.colors.successSecondary,
    fg: (t) => t.colors.successDark1,
  },
  tukipyynto_suljettu: {
    teksti: "Tukipyyntö suljettu",
    bg: (t) => t.colors.depthLight2,
    fg: (t) => t.colors.depthDark1,
  },
  tukipyynto_ratkaistu: {
    teksti: "Tukipyyntö ratkaistu",
    bg: (t) => t.colors.successSecondary,
    fg: (t) => t.colors.successDark1,
  },
  muistiinpano_lisatty: {
    teksti: "Muistiinpano lisätty",
    bg: (t) => t.colors.warningLight1,
    fg: (t) => t.colors.blackBase,
  },
  laite_deaktivoitu: {
    teksti: "Laite deaktivoitu",
    bg: (t) => t.colors.depthLight2,
    fg: (t) => t.colors.depthDark1,
  },
};

const Empty = styled.p`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  color: ${({ theme }) => theme.colors.depthDark1};
  font-style: italic;
  margin: 0;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.m};
  padding: ${({ theme }) => theme.spacing.s} 0;

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.depthLight2};
  }
`;

const Label = styled.span<{ $bg: string; $fg: string }>`
  display: inline-block;
  flex-shrink: 0;
  margin-top: 2px;
  background: ${({ $bg }) => $bg};
  color: ${({ $fg }) => $fg};
  padding: ${({ theme }) => theme.spacing.xxs}
    ${({ theme }) => theme.spacing.xs};
  font: ${({ theme }) => theme.typography.actionElementInnerTextBold};
  font-size: 0.75rem;
  white-space: nowrap;
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const Description = styled.div`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  color: ${({ theme }) => theme.colors.blackBase};
`;

const Reason = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xs};
  background: ${({ theme }) => theme.colors.depthLight3};
  border-left: 4px solid ${({ theme }) => theme.colors.depthLight1};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.s};
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.blackBase};
`;

const ReasonLabel = styled.span`
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.75rem;
`;

const Meta = styled.div`
  flex-shrink: 0;
  text-align: right;
`;

const Tekija = styled.div`
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.blackBase};
`;

const Aika = styled.div`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.depthDark1};
  margin-top: ${({ theme }) => theme.spacing.xxs};
`;

export default function AuditTrail({ entries }: { entries: AuditEntry[] }) {
  const { t } = useTranslation(["kayttajaKortti", "common"]);
  const theme = useTheme();
  if (entries.length === 0) {
    return <Empty>{t("ei.audit_merkintoja")}</Empty>;
  }

  // Sort entries in reverse chronological order (newest first)
  const sortedEntries = [...entries].sort((a, b) => {
    const timeA = new Date(normalizeTimestamp(a.aika)).getTime();
    const timeB = new Date(normalizeTimestamp(b.aika)).getTime();
    return timeB - timeA; // Newest first
  });
  return (
    <List>
      {sortedEntries.map((e) => {
        const meta = tyyppiMeta[e.tyyppi] ?? {
          teksti: e.tyyppi,
          bg: (t: DefaultTheme) => t.colors.depthLight2,
          fg: (t: DefaultTheme) => t.colors.depthDark1,
        };
        return (
          <Row key={e.id}>
            <Label $bg={meta.bg(theme)} $fg={meta.fg(theme)}>
              {t(`audit.${e.tyyppi}`)}
            </Label>
            <Body>
              <Description>{e.kuvaus}</Description>
              {e.peruste && (
                <Reason>
                  <ReasonLabel>Peruste: </ReasonLabel>
                  {e.peruste}
                </Reason>
              )}
            </Body>
            <Meta>
              <Tekija>{e.tekija}</Tekija>
              <Aika>{formatAikaleima(e.aika)}</Aika>
            </Meta>
          </Row>
        );
      })}
    </List>
  );
}
